import { config } from '@config/config';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const initGA = () => {
  const trackingId = config.GOOGLE_ANALYTICS_ID;
  if (!trackingId) return;

  if (document.getElementById('google-analytics-script')) return;

  const script = document.createElement('script');
  script.id = 'google-analytics-script';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', trackingId, {
    send_page_view: false, // manually track page views on route changes
  });
};

export const trackPageView = (path: string) => {
  const trackingId = config.GOOGLE_ANALYTICS_ID;
  if (trackingId && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
};

export const setGAUser = (userId: string) => {
  const trackingId = config.GOOGLE_ANALYTICS_ID;
  if (!trackingId || !window.gtag) return;
  window.gtag('config', trackingId, { user_id: userId });
};

// Clear the user identity from GA (on logout)
export const clearGAUser = () => {
  const trackingId = config.GOOGLE_ANALYTICS_ID;
  if (!trackingId || !window.gtag) return;
  window.gtag('config', trackingId, { user_id: undefined });
};

export type GAEventParams = Record<string, string | number | boolean | undefined>;

export const trackEvent = (eventName: string, params?: GAEventParams) => {
  if (!config.GOOGLE_ANALYTICS_ID || !window.gtag) return;
  window.gtag('event', eventName, params ?? {});
};

export const trackLogin = (userId: string, method: string = 'email') => {
  setGAUser(userId);
  trackEvent('login', { method, user_id: userId });
};

export const trackLogout = () => {
  trackEvent('logout');
  clearGAUser();
};

export const trackSignUp = (method: string = 'email') => {
  trackEvent('sign_up', { method });
};

export const trackPayLinkCreated = (invoiceId: string, amount?: number, currency?: string) => {
  trackEvent('paylink_created', {
    invoice_id: invoiceId,
    value: amount,
    currency,
  });
};

export const trackPayLinkUpdated = (invoiceId: string) => {
  trackEvent('paylink_updated', { invoice_id: invoiceId });
};

export const trackPayLinkViewed = (invoiceId: string) => {
  trackEvent('paylink_viewed', { invoice_id: invoiceId });
};

export const trackPayoutViewed = (payoutId: string) => {
  trackEvent('payout_viewed', { payout_id: payoutId });
};

export const trackTransferCreated = (transferId: string, amount?: number, currency?: string) => {
  trackEvent('transfer_created', {
    transfer_id: transferId,
    value: amount,
    currency,
  });
};

export const trackCheckoutViewed = (checkoutId: string) => {
  trackEvent('checkout_viewed', { checkout_id: checkoutId });
};

export const trackDisputeCreated = (disputeId: string, reason?: string) => {
  trackEvent('dispute_created', { dispute_id: disputeId, reason });
};

export const trackDisputeEvidenceSubmitted = (disputeId: string) => {
  trackEvent('dispute_evidence_submitted', { dispute_id: disputeId });
};

export const trackRefundUpdated = (refundId: string) => {
  trackEvent('refund_updated', { refund_id: refundId });
};

export const trackRefundInitiated = (paymentId: string, amount?: number, currency?: string) => {
  trackEvent('refund_initiated', { payment_id: paymentId, value: amount, currency });
};

export const trackError = (errorContext: string, errorMessage: string) => {
  trackEvent('app_error', {
    error_context: errorContext,
    error_message: errorMessage.slice(0, 150), // GA param limit
  });
};
